import React, { useState, useEffect } from "react";
import { Tooltip } from "antd";
import styles from "./tabs.module.css";

export interface TabInterface {
    content: React.ReactNode;
    title: React.ReactNode;
    disabled?: boolean;
    reason?: string;
}

interface TabsInterface {
    tabs: TabInterface[];
    tabHeaderClassName?: string;
    className?: string;
    currentIndex?: number;
    onChange?: (tab: TabInterface, index: number) => void;
}

const Tabs: React.FunctionComponent<TabsInterface> = ({
    tabs,
    tabHeaderClassName,
    className,
    currentIndex,
    onChange,
}: TabsInterface) => {
    // States

    const [_currentIndex, setCurrentIndex] = useState<number | undefined>(
        currentIndex
    );

    // Effects

    useEffect(() => {
        setCurrentIndex(currentIndex);
    }, [currentIndex]);

    useEffect(() => {
        if (_currentIndex === undefined || !onChange) return;

        onChange(tabs[_currentIndex], _currentIndex);
    }, [_currentIndex]);

    useEffect(() => {
        if (_currentIndex === undefined) return;

        if (tabs.length <= _currentIndex) setCurrentIndex(0);
    }, [tabs]);

    // Rendering

    let rootClass = styles.holder;
    if (className) rootClass += ` ${className}`;

    const tabContents = tabs.map(({ content }: TabInterface, index) => (
        <div key={index}>{content}</div>
    ));

    return (
        <div className={rootClass}>
            <div
                className={`${styles.containerOverride} container ${tabHeaderClassName}`}
            >
                <div className={styles.header}>
                    {tabs.map(({ title, disabled, reason }, index) => (
                        <Tooltip
                            key={index}
                            title={disabled && reason}
                            placement="bottomRight"
                        >
                            <button
                                onClick={() => {
                                    if (disabled) return;

                                    if (
                                        tabs.length === 1 &&
                                        _currentIndex === index
                                    ) {
                                        setCurrentIndex(undefined);
                                        return;
                                    }

                                    setCurrentIndex(index);
                                }}
                                className={`${
                                    _currentIndex === index ? styles.active : ""
                                } ${disabled ? styles.disabled : ""}`}
                            >
                                <h4>{title}</h4>
                            </button>
                        </Tooltip>
                    ))}
                </div>
            </div>
            <div>
                {_currentIndex === undefined
                    ? null
                    : tabContents[_currentIndex]}
            </div>
        </div>
    );
};

export default Tabs;
